import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import HeroDemo from './HeroDemo';
import snapshot from '../demo/snapshot.json';
import { day } from '../format';
import './LandingPage.css';

/* ── Signal ticker ─────────────────────────────────────────────────────────
 *
 * Frozen to the same committed week as the hero demo, and for the same reason:
 * it read from /api/preview, so the strip along the bottom of the marketing
 * page changed every Monday and depended on a database that autosuspends. One
 * line per company at its best term, largest first - the shaping this used to
 * do to the live feed now happens once, in build_demo_snapshot.py. */

const LiveTicker = () => {
  // Doubled so the marquee has something to scroll into as it loops.
  const loop = [...snapshot.ticker, ...snapshot.ticker];

  return (
    <div className="ticker-section" aria-hidden="true">
      <div className="ticker-label">
        <span className="live-dot" />
        Week ending {day(snapshot.week_end)}
      </div>
      <div className="ticker-viewport">
        <div className="ticker-track">
          {loop.map((item, i) => (
            <span className="ticker-item" key={i}>
              <span className="ticker-symbol">{item.ticker}</span>
              <span className="ticker-term">{item.term}</span>
              <span className="ticker-pct">+{item.pct.toLocaleString()}%</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const SPARKLES = [
  { left: '30%', top: '18%', delay: '0s' },
  { left: '68%', top: '10%', delay: '1.2s' },
  { left: '42%', top: '4%', delay: '2.1s' },
  { left: '58%', top: '30%', delay: '0.7s' },
  { left: '25%', top: '38%', delay: '1.8s' },
  { left: '74%', top: '34%', delay: '2.6s' },
];

/* ── Page ──────────────────────────────────────────────────────────────────── */

const LandingPage = () => {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  /* One door for someone who already has an account. Offering a signed-in
   * reader both "get started" and "view signals" made them choose between two
   * buttons that went to the same place. */
  const enter = () => navigate('/trends');
  const join = () => navigate('/signup');

  return (
    <div className="landing-page">
      {/* Hero: headline, then the lit stage with the product on it */}
      <section className="landing-hero">
        <div className="hero-stars" />
        <div className="hero-stars hero-stars-2" />

        <div className="container hero-content">
          <h1 className="hero-title">
            Spot emerging trends
            <br />
            before the market does
          </h1>
          <p className="hero-subtitle">
            Inflection tracks 21,000+ brand and product searches across 1,500+ consumer-facing
            companies to surface the demand signals that move stocks.
          </p>
          <div className="hero-actions">
            {isSignedIn ? (
              <button className="cta-button" onClick={enter}>
                Open Platform
                <ArrowRight size={16} />
              </button>
            ) : (
              <>
                <button className="cta-button" onClick={join}>
                  Sign Up Free
                  <ArrowRight size={16} />
                </button>
                {/* Sending a stranger to a sign-in form and calling it "view
                    live signals" was a bait. /preview shows this week's actual
                    trends, three of them in full, so the promise is kept
                    before the ask. */}
                <button className="cta-button-ghost" onClick={() => navigate('/preview')}>
                  View live signals
                </button>
              </>
            )}
          </div>
        </div>

        <div className="preview-stage">
          <div className="stage-glow" />
          <div className="stage-glow-core" />

          {SPARKLES.map((s, i) => (
            <span key={i} className="stage-sparkle"
              style={{ left: s.left, top: s.top, animationDelay: s.delay }} />
          ))}

          {/* Was twelve grey bars and a drawn curve. A stranger's first question
              is whether there is anything behind the marketing, and a mock is
              the one answer that cannot settle it. The frame, its chrome, the
              address and the tabs under it all belong to the demo. */}
          <HeroDemo />
        </div>
      </section>

      {/* Stats */}
      <section className="stats-section">
        <div className="container">
          {/* Scale first, then the three filters a term has to survive, in the
            * order it meets them. A visitor can reach the real list in one
            * click, so anything overstated here gets found: 21,123 series are
            * polled weekly and 48 countries carry them. */}
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">21,000+</div>
              <div className="stat-label">
                Search terms tracked weekly across 48 countries
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-number">Statistical</div>
              <div className="stat-label">
                Robust trend detection to filter out noisy terms
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-number">Volume</div>
              <div className="stat-label">
                Google search volume to capture absolute demand
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-number">LLM validated</div>
              <div className="stat-label">
                Rich context scored for materiality to the company
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="section-title">Pricing</h2>
          <p className="pricing-subtitle">Start exploring for free. Premium tiers coming soon.</p>
          <div className="pricing-grid">
            <div className="pricing-card pricing-card-active">
              <h3 className="pricing-plan-name">Free</h3>
              <div className="pricing-price">
                <span className="pricing-amount">$0</span>
                <span className="pricing-period">/ month</span>
              </div>
              <ul className="pricing-features">
                <li>All trend signals</li>
                <li>Statistical trend classification</li>
                <li>AI analysis summaries</li>
                <li>5-year trend charts</li>
                <li>Weekly refresh</li>
              </ul>
              <button className="cta-button pricing-cta" onClick={isSignedIn ? enter : join}>
                {isSignedIn ? 'Open Platform' : 'Sign Up Free'}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Ticker (quiet, GitHub logo-marquee position) */}
      <LiveTicker />

      {/* Bottom CTA */}
      <section className="cta-section">
        <div className="cta-glow" />
        <div className="container">
          <h2 className="cta-title">Ready to find your edge?</h2>
          <p className="cta-text">
            Join for free and start exploring trend signals across thousands of companies.
          </p>
          <button className="cta-button" onClick={isSignedIn ? enter : join}>
            {isSignedIn ? 'Open Platform' : 'Sign Up Free'}
            <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
